export type TravelMode = "walking" | "driving";

export type RouteStop = { lat: number; lng: number };

/**
 * Máximo de paradas por enlace de Google Maps, contando el destino pero no el origen
 * (el origen del primer tramo es el punto de partida; el de los siguientes, la última
 * parada del tramo anterior). 9 es el valor pedido por Ian, NO verificado: el límite
 * documentado de `maps/dir/?api=1` es 23 waypoints + origen + destino (25 puntos),
 * pendiente de probar a mano con una ruta real de 12+ paradas en el celular (PLAN_RUTAS
 * §E). Cambiar solo esta constante para ajustarlo.
 */
export const MAX_PARADAS_MAPS = 9;

const coord = (p: RouteStop) => `${p.lat},${p.lng}`;

const buildSegmentUrl = (
  origin: RouteStop,
  waypoints: RouteStop[],
  destination: RouteStop,
  mode: TravelMode,
): string => {
  const params = new URLSearchParams({
    api: "1",
    origin: coord(origin),
    destination: coord(destination),
    travelmode: mode,
  });
  if (waypoints.length > 0) {
    params.set("waypoints", waypoints.map(coord).join("|"));
  }
  return `https://www.google.com/maps/dir/?${params.toString()}`;
};

export type RouteSegment = {
  /** 1-based, para etiquetar botones ("Tramo 1 de 2") */
  index: number;
  /** cantidad total de tramos de la ruta */
  total: number;
  url: string;
};

/**
 * Parte `stops` (sin contar el punto de partida) en tramos de hasta `maxStops`
 * paradas cada uno (la última es el destino), y genera el enlace de Google Maps de
 * cada tramo. El destino de un tramo es el origen del siguiente, para no
 * perder continuidad al cruzar entre enlaces.
 */
export const googleMapsRouteSegments = (
  start: RouteStop,
  stops: RouteStop[],
  mode: TravelMode,
  maxStops = MAX_PARADAS_MAPS,
): RouteSegment[] => {
  if (stops.length === 0) return [];

  const size = Math.max(1, Math.floor(maxStops));
  const segments: Omit<RouteSegment, "total">[] = [];
  let origin = start;
  let i = 0;
  let index = 1;

  while (i < stops.length) {
    const chunk = stops.slice(i, i + size);
    const destination = chunk[chunk.length - 1];
    const waypoints = chunk.slice(0, -1);
    segments.push({
      index,
      url: buildSegmentUrl(origin, waypoints, destination, mode),
    });
    origin = destination;
    i += chunk.length;
    index += 1;
  }

  return segments.map((seg) => ({ ...seg, total: segments.length }));
};
